import { z } from 'zod';
import { Ulid } from './common.js';
/**
 * Printed figures (docs/meshy-models-plan.md, docs/meshy-models-architecture.md).
 *
 * A game's playground agent drafts a *set* of figures as one concept sheet, a person approves the
 * sheet and its crops, and BoardFlight makes each figure with Meshy on its own key: a painted model
 * for the shop and the painter, and an unpainted, repaired, Base-sized mesh for the printer. Each
 * figure is a topper for one numbered Board Base, and a Base's number is its glyph ID.
 *
 * Everything a figure's shape depends on is declared here or measured by Meshy. The Worker never
 * opens a model; it records what Meshy said about one.
 */
/**
 * The Board Base a figure stands on (architecture §0.16, §5.5).
 *
 * The size is the owner's (2026-10-28). The tallest a figure may be without tipping a Base is F0's
 * to measure: until a printed figure has stood on a Base on a Board, `figureHeightMaxMm` is `null`
 * and the built-in range below is the limit. Once F0 writes one, it replaces the built-in top.
 */
export const BOARD_BASE = {
    topDiameterMm: 25,
    heightMm: 2,
    /** A figure's widest horizontal extent may not exceed this: it never overhangs its Base. */
    footprintMaxMm: 25,
    figureHeightMinMm: null,
    figureHeightMaxMm: null,
    /** The size was given on this day; the height range and the mount are still open. */
    measuredAt: '2026-10-28',
};
/**
 * What a piece is, which decides how it is drawn and what its size means (owner, 2026-10-29):
 *
 *  - `standing`: a character, a creature, a tree, a tower. Drawn full body from the front; its size
 *    is its height.
 *  - `flat`: a token, a tile, a coin, a crop. Drawn lying flat, seen from above; its size is its
 *    thickness, and its width is the Base's at most, like every piece's.
 */
export const FIGURE_FORMS = ['standing', 'flat'];
export const FigureForm = z.enum(FIGURE_FORMS);
/**
 * The built-in sizes, from what a 25 mm Base carries. A 25 mm round base is the miniatures hobby's
 * standard base for "28 mm scale" figures, which is where a person-sized character sits; the top is
 * a guess at what tips a Base on a Board's glass, until F0 measures it.
 */
export const DEFAULT_FIGURE_SIZES = {
    standing: { minMm: 8, maxMm: 60 },
    flat: { minMm: 2, maxMm: 12 },
};
/**
 * The sizes a piece may be drafted at, always: the built-in ranges, the measured Base's top once F0
 * writes one, narrowed by an operator's limits from the console's figure settings when both ends are
 * set and leave something. The agent picks each piece's size inside them; nobody has to set anything
 * for drafting to work.
 */
export function figureSizes(limits) {
    let standing = {
        minMm: BOARD_BASE.figureHeightMinMm ?? DEFAULT_FIGURE_SIZES.standing.minMm,
        maxMm: BOARD_BASE.figureHeightMaxMm ?? DEFAULT_FIGURE_SIZES.standing.maxMm,
    };
    const min = limits?.heightMinMm ?? null;
    const max = limits?.heightMaxMm ?? null;
    if (min !== null && max !== null && min <= max) {
        const narrowed = { minMm: Math.max(standing.minMm, min), maxMm: Math.min(standing.maxMm, max) };
        if (narrowed.minMm <= narrowed.maxMm)
            standing = narrowed;
    }
    return { standing, flat: { ...DEFAULT_FIGURE_SIZES.flat } };
}
/**
 * A piece's form, read from the set's plan it was made from (`figure_sheets.manifest`): a figure row
 * keeps its key, and its sheet keeps what each key is. Standing for a plan written before forms.
 */
export function formOf(manifestJson, key) {
    try {
        const items = JSON.parse(manifestJson ?? '[]');
        const item = Array.isArray(items) ? items.find((one) => one?.key === key) : undefined;
        return item?.form === 'flat' ? 'flat' : 'standing';
    }
    catch {
        return 'standing';
    }
}
/** What a size means in words: `37.5 mm tall`, `4 mm thick`. */
export function figureSizeWords(form, mm) {
    return form === 'flat' ? `${mm} mm thick` : `${mm} mm tall`;
}
/**
 * The height a figure is printed at so it fits its Base, from Meshy's own measurement.
 *
 * `dimensionsMm` is analyze's `dimensions_mm` at `requestedMm`: `[width, depth, height]`, Z-up, as
 * the F0 spike measured (architecture §5.1). When the wider of the first two is over the Base's
 * footprint, the figure is scaled down until it fits, rounded down to half a millimetre.
 */
export function fittedHeightMm(requestedMm, dimensionsMm) {
    const widest = Math.max(dimensionsMm[0] ?? 0, dimensionsMm[1] ?? 0);
    if (!(widest > BOARD_BASE.footprintMaxMm))
        return requestedMm;
    return Math.floor(((requestedMm * BOARD_BASE.footprintMaxMm) / widest) * 2) / 2;
}
/**
 * What each step costs in Meshy credits (docs.meshy.ai pricing, read 2026-10-28; the spike paid
 * exactly these). A figure's quote assumes a repair, which the release at its end gives back when
 * none was needed.
 */
export const FIGURE_CREDITS = {
    sheet: 9,
    toModel: 30,
    repair: 10,
    resize: 1,
    convert: 1,
};
export const FIGURE_QUOTE_CREDITS = FIGURE_CREDITS.toModel + FIGURE_CREDITS.repair + FIGURE_CREDITS.resize + FIGURE_CREDITS.convert;
/** Concept sheets one game may draft in 24 hours: the one spend a token can cause (§7.1). */
export const FIGURE_SHEETS_PER_DAY = 6;
export const FIGURE_SET_LIMITS = { minFigures: 2, maxFigures: 8 };
const FIGURE_SET_LIMITS_MAX_FIGURES = FIGURE_SET_LIMITS.maxFigures;
/**
 * Prepended to every sheet prompt by the Worker, so a manifest that leaves it out is still made in
 * the house style (architecture §7.2). The colours are the paint: the painter works from them.
 */
export const FIGURE_SHEET_STYLE = 'A lineup of tabletop pieces for a board game, each alone, evenly spaced in one row with wide ' +
    'white space between them, plain pure white background. A standing figure is shown full body in ' +
    'a front three-quarter view; a flat piece lies flat on the ground, seen from slightly above. ' +
    'Painted like hand-painted tabletop miniatures in the game palette: flat matte colours, clear ' +
    'separate colour regions, no gradients. No base, no plinth, no text, no scenery, no shadows on ' +
    'the ground, thick limbs and no thin protrusions, arms close to the body, readable at the size of ' +
    'a fingertip.';
/* ------------------------------------------------------------------- plinths (figure-plinths-plan) */
/**
 * The plinth library (docs/figure-plinths-plan.md §2): twenty textured grounds a printed figure
 * stands on, one chosen per set. A plinth is part of the topper, a disc `PLINTH.diameterMm` across and
 * `PLINTH.baseMm` tall with a relief up to `PLINTH.reliefMaxMm` above that.
 *
 * **This is the only list.** The dashboard's chooser, the agent's tool, the API's validation and the
 * console read it; the assembler builds each design from its recipe in `figure-assembler/plinths.py`,
 * whose build script writes `apps/web/public/plinths/plinths.json`, and a test holds that file to
 * this list. The previews are `/plinths/<key>.webp` and `/plinths/<key>.glb` on the dashboard's host.
 *
 * `PLINTH_LIBRARY_VERSION` changes only when a recipe does, and then the assembler's image is
 * published again: a job names the version its set was reviewed at.
 */
export const PLINTH_LIBRARY_VERSION = 1;
export const PLINTH = { diameterMm: 25, baseMm: 2, reliefMaxMm: 1 };
export const PLINTH_DESIGNS = [
    { key: 'plain', name: 'Plain', line: 'a smooth top, for any game' },
    { key: 'grass', name: 'Meadow', line: 'short tufts of grass' },
    { key: 'cobbles', name: 'Cobblestones', line: 'rounded stones and mortar lines' },
    { key: 'flagstone', name: 'Flagstone', line: 'large flat slabs, cracks between' },
    { key: 'planks', name: 'Wooden planks', line: 'boards with grain and nail heads' },
    { key: 'sand', name: 'Sand', line: 'soft wind ripples' },
    { key: 'snow', name: 'Snow', line: 'drifts and footprints' },
    { key: 'gravel', name: 'Gravel', line: 'fine scattered stones' },
    { key: 'cracked', name: 'Cracked earth', line: 'dry clay plates' },
    { key: 'forest', name: 'Forest floor', line: 'roots, fallen leaves, a pebble' },
    { key: 'rocky', name: 'Rocky ground', line: 'broken rock and scree' },
    { key: 'dungeon', name: 'Dungeon tiles', line: 'square flagstones, worn edges' },
    { key: 'brick', name: 'Brick', line: 'running-bond bricks' },
    { key: 'hex', name: 'Hex tiles', line: 'a hexagon grid' },
    { key: 'lava', name: 'Lava rock', line: 'cooled flow with glowing cracks' },
    { key: 'ice', name: 'Ice', line: 'smooth sheets with sharp fractures' },
    { key: 'swamp', name: 'Swamp', line: 'mud ripples, a puddle, reed stubs' },
    { key: 'deck', name: 'Deck plate', line: 'tread plate with a riveted seam' },
    { key: 'waves', name: 'Waves', line: 'a stylised water surface' },
    { key: 'runes', name: 'Rune circle', line: 'an engraved arcane ring' },
];
export const PlinthKey = z.enum(PLINTH_DESIGNS.map((design) => design.key));
export function plinthDesign(key) {
    return PLINTH_DESIGNS.find((design) => design.key === key) ?? null;
}
/** A set's plinth, chosen by a person or the agent. Free, so a session token may set it. */
export const SetFigurePlinthRequest = z.object({ plinth: PlinthKey });
/**
 * The two bottoms a topper is made with (§0.5): `snap` has a knob that clicks into Boardaux's TPU snap
 * piece, `flat` rests on the Base. Every figure of a set is assembled both ways; the buyer chooses.
 */
export const FIGURE_MOUNTS = ['snap', 'flat'];
export const FigureMount = z.enum(FIGURE_MOUNTS);
export const FIGURE_MOUNT_WORDS = {
    snap: { name: 'Snap-on', line: 'Clicks onto your Board Base. Includes a snap piece for each figure.' },
    flat: { name: 'Flat', line: 'Rests on your Board Base.' },
};
/** One check the assembler ran (figure-assembler/assemble.py `Check`), as it reported it. */
export const AssemblyCheck = z.object({
    name: z.string().max(64),
    ok: z.boolean(),
    measured: z.unknown(),
    expected: z.string().max(200),
});
/** One figure on one bottom, as the runner reported it (`assembled.py`, "The report"). */
export const AssemblyMountReport = z.object({
    report: z
        .object({
        plinth: z.string(),
        library_version: z.number().int(),
        glyph: z.number().int(),
        bottom: FigureMount,
        dimensions_mm: z.array(z.number()).max(3),
        triangles: z.number().int().nonnegative(),
        volume_mm3: z.number(),
        placement_mm: z.array(z.number()).max(3),
        overhang_mm: z.number(),
        checks: z.array(AssemblyCheck).max(20),
    })
        .passthrough(),
    files: z
        .partialRecord(z.enum(['3mf', 'stl']), z.object({ sha256: z.string().regex(/^[0-9a-f]{64}$/), size: z.number().int().positive() }))
        .optional(),
});
/**
 * `POST /v1/figure-assembly/:jobId/report`: the runner's whole report, once per job, with the job
 * file's one-shot token. `ok` is true only when every figure assembled on every bottom.
 */
export const AssemblyReport = z.object({
    ok: z.boolean(),
    error: z.string().max(4000).optional(),
    libraryVersion: z.number().int().optional(),
    figures: z
        .array(z.object({
        figureId: z.string().max(64),
        ok: z.boolean(),
        error: z.string().max(4000).optional(),
        // Partial: a figure that failed has no bottom, or only some (`assembled.py` reports `{}`).
        mounts: z.partialRecord(FigureMount, AssemblyMountReport),
    }))
        .max(FIGURE_SET_LIMITS_MAX_FIGURES),
    log: z.string().max(20_000).optional(),
});
export const FIGURE_ASSEMBLY_STATUSES = ['queued', 'running', 'done', 'failed'];
/** A key a manifest names a figure by: stable across retakes, short, and safe in a path. */
export const FigureKey = z
    .string()
    .regex(/^[a-z0-9][a-z0-9-]{0,31}$/, 'A figure key is lower-case letters, digits and dashes.');
export const FigureManifestItem = z.object({
    key: FigureKey,
    name: z.string().trim().min(1).max(60),
    /** What it stands for in play, in the game's words. */
    role: z.string().trim().min(1).max(120),
    /** One line a sculptor could work from. */
    description: z.string().trim().min(1).max(300),
    /** Standing (drawn full body, sized by height) or flat (drawn lying flat, sized by thickness). */
    form: FigureForm.default('standing'),
    /** A standing piece's printed height, or a flat piece's thickness, in mm (`figureSizes`). */
    heightMm: z.number().positive().max(200),
    /** The Base it goes on: Bases are printed with their glyph ID. Unique in a set. */
    glyph: z.number().int().min(1).max(999),
});
export const FigureManifest = z
    .array(FigureManifestItem)
    .min(FIGURE_SET_LIMITS.minFigures)
    .max(FIGURE_SET_LIMITS.maxFigures)
    .superRefine((items, ctx) => {
    const keys = new Set();
    const glyphs = new Set();
    items.forEach((item, index) => {
        if (keys.has(item.key))
            ctx.addIssue({ code: 'custom', path: [index, 'key'], message: 'Each figure needs its own key.' });
        if (glyphs.has(item.glyph)) {
            ctx.addIssue({ code: 'custom', path: [index, 'glyph'], message: 'Two figures of one set cannot share a Base.' });
        }
        keys.add(item.key);
        glyphs.add(item.glyph);
    });
});
export const DraftFigureSheetRequest = z.object({
    /** The set's name; a new name starts a new set. */
    set: z.string().trim().min(1).max(60),
    manifest: FigureManifest,
    /** The game's look in a sentence or two: palette, shapes, mood. Appended after the house style. */
    style: z.string().trim().max(800).optional(),
    /** The plinth the agent suggests for this set's look; the developer can change it until submitting. */
    plinth: PlinthKey.optional(),
});
/** A box in sheet pixels. */
export const FigureCrop = z.object({
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    w: z.number().int().min(16),
    h: z.number().int().min(16),
});
export const ProposeFigureCropsRequest = z.object({
    crops: z.record(FigureKey, FigureCrop),
});
/** The quote the person was shown. A stale one is refused rather than charged. */
export const ApproveFigureSheetRequest = z.object({ quote: z.number().int().min(0) });
export const ProposeFigureRetakeRequest = z.object({
    note: z.string().trim().min(1).max(500),
    crop: FigureCrop.optional(),
});
export const FigureSetStatus = z.enum([
    'drafting',
    'approved',
    'ready',
    'submitted',
    'returned',
    'accepted',
    'listed',
    'unlisted',
]);
/** A set may change its figures only before an operator has accepted it (§4.1). */
export const FIGURE_SET_LOCKED = ['submitted', 'accepted', 'listed', 'unlisted'];
/** `FigurePrintCheck` from the answer Meshy gave, as stored; null when there is none to read. */
export function figurePrintCheck(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const value = raw;
    if (typeof value.status !== 'string')
        return null;
    const number = (input) => (typeof input === 'number' && Number.isFinite(input) ? input : null);
    const metrics = value.metrics ?? {};
    return {
        status: value.status,
        printer: typeof value.intended_print?.printer_type === 'string' ? value.intended_print.printer_type : null,
        heightMm: number(value.intended_print?.height_mm),
        wallMm: number(value.intended_print?.wall_thickness_mm),
        thinShare: number(value.thin_walls?.thin_area_ratio),
        thinWallsFail: value.thin_walls?.status === 'error',
        holes: number(metrics.holes) ?? 0,
        brokenEdges: number(metrics.non_manifold_edges) ?? 0,
        sealed: typeof metrics.is_watertight === 'boolean' ? metrics.is_watertight : null,
    };
}
/* ------------------------------------------------------------------- the admin console (F2) */
/** An operator sending a submitted set back says why, in words the team reads verbatim. */
export const ReturnFigureSetRequest = z.object({ note: z.string().trim().min(1).max(2000) });
/** A game's figures switch and monthly Meshy credits, set by an operator (§0.4, §0.17). */
export const SetFiguresShopRequest = z.object({
    on: z.boolean(),
    creditsMonthly: z.number().int().min(0).max(100_000).optional(),
});
/**
 * The files an operator can open for one figure: Meshy's model and renders, and what the assembler
 * joined it into on each bottom. Every one was made by the pipeline; an operator uploads none of them.
 */
export const ADMIN_FIGURE_FILES = [
    'print.3mf',
    'print.stl',
    'assembled.snap.3mf',
    'assembled.snap.stl',
    'assembled.flat.3mf',
    'assembled.flat.stl',
    'view.glb',
    'painted.glb',
    'front.png',
    'right.png',
    'back.png',
    'left.png',
];
/* ------------------------------------------------------------------------------ the shop (F4) */
/**
 * How a set is sold (§0.13): as printed, or hand-painted from its painted model. A set has a price
 * per finish it is sold in, and a photo of each.
 */
export const FigureFinish = z.enum(['unpainted', 'painted']);
export const FIGURE_FINISHES = ['unpainted', 'painted'];
/** The most of one set in one finish an order may hold, and the most lines an order may have. */
export const SHOP_MAX_QUANTITY = 10;
export const SHOP_MAX_ITEMS = 10;
/** Unpaid checkouts one account may have open at once; one more is refused (§9.1). */
export const SHOP_MAX_OPEN_ORDERS = 4;
/** An operator's prices for a set, one per finish; a finish left out is not sold. */
export const SetFigurePricesRequest = z.object({
    prices: z
        .array(z.object({ finish: FigureFinish, priceCents: z.number().int().min(100).max(100_000) }))
        .max(2)
        .refine((list) => new Set(list.map((one) => one.finish)).size === list.length, 'One price per finish.'),
});
export const ShopCheckoutRequest = z.object({
    items: z
        .array(z.object({
        setId: Ulid,
        finish: FigureFinish,
        /** The bottom (§0.5). Same price either way. */
        mount: FigureMount.default('snap'),
        quantity: z.number().int().min(1).max(SHOP_MAX_QUANTITY),
    }))
        .min(1)
        .max(SHOP_MAX_ITEMS)
        .refine((list) => new Set(list.map((one) => `${one.setId}:${one.finish}:${one.mount}`)).size === list.length, 'Each set, finish and bottom once; say how many in its quantity.'),
});
export const ORDER_STATUSES = ['open', 'paid', 'making', 'shipped', 'expired', 'refunded'];
/** An operator marking an order shipped gives its tracking number. */
export const ShipOrderRequest = z.object({ tracking: z.string().trim().min(1).max(200) });
/* ------------------------------------------------------------- the deployment's settings (console) */
/** Where a set ships: the US only (§0.14). A decision, not a setting. */
export const SHOP_COUNTRIES = ['US'];
export const FIGURE_PRINTER_TYPES = ['sla', 'fdm', 'full_color'];
export const DEFAULT_FIGURE_SETTINGS = {
    shopOpen: false,
    leadDays: 14,
    shippingRates: [],
    tax: true,
    meshyLowBalance: 500,
    meshyConcurrency: 4,
    printerType: 'sla',
    heightMinMm: null,
    heightMaxMm: null,
    knobDiameterMm: 10,
    knobHeightMm: 1.4,
    engraveDepthMm: 0.5,
    snapPiece: false,
    updatedAt: null,
    updatedBy: null,
};
/** An operator's change to the settings: any of them, each checked as the table checks it. */
export const UpdateFigureSettingsRequest = z
    .object({
    shopOpen: z.boolean(),
    leadDays: z.number().int().min(1).max(120),
    shippingRates: z.array(z.string().trim().regex(/^shr_[A-Za-z0-9_]+$/, 'A Stripe shipping rate id starts with shr_.')).max(5),
    tax: z.boolean(),
    meshyLowBalance: z.number().int().min(0).max(1_000_000),
    meshyConcurrency: z.number().int().min(1).max(10),
    printerType: z.enum(FIGURE_PRINTER_TYPES),
    heightMinMm: z.number().positive().max(200).nullable(),
    heightMaxMm: z.number().positive().max(200).nullable(),
    // At most 12: wider, the knob meets the 4 mm Base number on the ring beside it (8.75 mm out).
    knobDiameterMm: z.number().min(6).max(12),
    knobHeightMm: z.number().min(0.4).max(3),
    engraveDepthMm: z.number().min(0.2).max(1),
})
    .partial()
    .refine((body) => body.heightMinMm === undefined ||
    body.heightMaxMm === undefined ||
    body.heightMinMm === null ||
    body.heightMaxMm === null ||
    body.heightMinMm <= body.heightMaxMm, 'The shortest height cannot be above the tallest.');
/** An operator marking a team as one of Boardaux's own, or not. */
export const SetFirstPartyRequest = z.object({ on: z.boolean() });
//# sourceMappingURL=figures.js.map